import { Injectable, signal } from '@angular/core';
import { ZohoBooksService } from '../../services/zoho-books-service/zoho-books-service.service';
import {
  ZohoTimeEntry,
  ZohoTimeEntriesByDate,
  ZohoTimeEntriesByWeek,
  ZohoTimeEntriesByMonth,
  groupZohoEntriesByDate,
  groupZohoEntriesByWeek,
  groupZohoEntriesByMonth,
} from '../../domain/zoho-books-domain';
import { finalize, map, switchMap, tap } from 'rxjs';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import { AuthenticationFeatureService } from '../authentication-feature/authentication-feature.service';

dayjs.extend(isBetween);
dayjs.extend(isoWeek);

export type ZohoGroupByDateTypes = 'day' | 'week' | 'month';

@Injectable({
  providedIn: 'root',
})
export class ZohoBooksFeatureService {
  isLoadingTimeEntries = signal<boolean>(false);
  isCheckingAuth = signal<boolean>(true);
  isConnected = signal<boolean>(false);
  timeEntries = signal<ZohoTimeEntry[]>([]);
  private _zohoUserId = signal<string | null>(null);

  selectedDate = signal<Dayjs>(dayjs());

  searchText = signal<string>('');
  groupByDateTypes = signal<ZohoGroupByDateTypes>('day');
  totalHoursAccumulated = 0;

  constructor(
    private _zohoBooksService: ZohoBooksService,
    private _authFeature: AuthenticationFeatureService,
  ) {}

  private getUserEmail(): string {
    return this._authFeature.getInstance().getActiveAccount()?.username ?? '';
  }

  checkAuthStatus() {
    const userEmail = this.getUserEmail();
    if (!userEmail) return;

    this.isCheckingAuth.set(true);
    return this._zohoBooksService.checkAuthStatus(userEmail).pipe(
      tap({
        next: (result) => {
          this.isConnected.set(result.connected);
          this.isCheckingAuth.set(false);
          if (result.connected) this._loadZohoUserId(userEmail);
        },
        error: () => this.isCheckingAuth.set(false),
      }),
    );
  }

  private _loadZohoUserId(userEmail: string): void {
    this._zohoBooksService.fetchZohoUserId(userEmail).subscribe({
      next: (res) => { if (res.user_id) this._zohoUserId.set(res.user_id); },
    });
  }

  connectZoho() {
    this._zohoBooksService.getAuthUrl().subscribe({
      next: (result) => (window.location.href = result.url),
    });
  }

  private getDateRange(): { startDate: Dayjs; endDate: Dayjs } {
    const date = this.selectedDate();
    const type = this.groupByDateTypes();
    if (type === 'week') return { startDate: date.startOf('isoWeek'), endDate: date.endOf('isoWeek') };
    if (type === 'month') return { startDate: date.startOf('month'), endDate: date.endOf('month') };
    return { startDate: date, endDate: date };
  }

  fetchTimeEntries() {
    this.isLoadingTimeEntries.set(true);
    const { startDate, endDate } = this.getDateRange();
    const userEmail = this.getUserEmail();
    const zohoUserId = this._zohoUserId();

    const fetch$ = (userId: string) =>
      this._zohoBooksService
        .fetchTimeEntries(startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'), userEmail, userId)
        .pipe(
          tap((root) => { this.timeEntries.set(root.time_entries ?? []); }),
          finalize(() => queueMicrotask(() => this.isLoadingTimeEntries.set(false))),
        );

    if (zohoUserId) return fetch$(zohoUserId);

    return this._zohoBooksService.fetchZohoUserId(userEmail).pipe(
      map(res => res.user_id ?? ''),
      tap(userId => { if (userId) this._zohoUserId.set(userId); }),
      switchMap(userId => fetch$(userId)),
    );
  }

  handleFilteringTimeEntries(entries: ZohoTimeEntry[]): ZohoTimeEntry[] {
    const search = this.searchText().toLowerCase();
    const { startDate, endDate } = this.getDateRange();

    return entries
      .filter((e) =>
        dayjs(e.log_date).isBetween(startDate, endDate, 'date') ||
        dayjs(e.log_date).isSame(startDate, 'date') ||
        dayjs(e.log_date).isSame(endDate, 'date'),
      )
      .filter((e) => {
        if (!search) return true;
        return (
          e.project_name?.toLowerCase().includes(search) ||
          e.task_name?.toLowerCase().includes(search) ||
          e.customer_name?.toLowerCase().includes(search) ||
          e.user_name?.toLowerCase().includes(search) ||
          e.notes?.toLowerCase().includes(search)
        );
      })
      .sort((a, b) => a.log_date.localeCompare(b.log_date));
  }

  groupByDate(entries: ZohoTimeEntry[]): ZohoTimeEntriesByDate[] {
    const filtered = this.handleFilteringTimeEntries(entries);
    const { grouped, totalHours } = groupZohoEntriesByDate(filtered);
    this.totalHoursAccumulated = totalHours;
    return grouped;
  }

  groupByWeek(entries: ZohoTimeEntry[]): ZohoTimeEntriesByWeek[] {
    return groupZohoEntriesByWeek(this.groupByDate(entries));
  }

  groupByMonth(entries: ZohoTimeEntry[]): ZohoTimeEntriesByMonth[] {
    return groupZohoEntriesByMonth(this.groupByDate(entries));
  }

  resetFilters() {
    this.selectedDate.set(dayjs());
    this.searchText.set('');
    this.groupByDateTypes.set('day');
  }
}
